import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Col, Row, Statistic, Table, Skeleton, Tag, Space, Button, Tooltip, Card, Select, message } from 'antd';
import { CloseOutlined, CheckOutlined, InfoCircleOutlined, ExclamationCircleFilled, SendOutlined } from '@ant-design/icons';
import { status_recibo_nomina } from '@app/constants/reciboNomina';
import { downloadRecibo } from '../../../../apis/TimbradoApi';
import { bitacoraProcesoNomina, recibosEncabezadoNomina } from '../../../../apis/ProcesoNominaApi';
import { processPoliza, getPoliza, sendToOdooPoliza } from '../../../../apis/PolizaApi';
import { getAll } from '../../../../apis/DefinicionPolizaApi';
import Currency from '../../../../components/common/Currency';

const { Option } = Select;

const Result = forwardRef(function Result({ header, form, values, disabledForm, onSendToOdoo, loading: parentLoading }, ref) {
    const [info, setInfo] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingPoliza, setLoadingPoliza] = useState(false);
    const [loadingDefiniciones, setLoadingDefiniciones] = useState(false);
    const [polizaGenerada, setPolizaGenerada] = useState(false);
    const [loadingEnviando, setLoadingEnviando] = useState(false);
    const [openDetail, setOpenDetail] = useState(false);
    const [periodSelected, setPeriodSelected] = useState(null);
    const [polizaObject, setPolizaObject] = useState(null);
    const [polizaData, setPolizaData] = useState(null);
    const [polizaEstado, setPolizaEstado] = useState(null);
    const [definicionesPoliza, setDefinicionesPoliza] = useState([]);
    const [definicionSeleccionada, setDefinicionSeleccionada] = useState(null);
    const [hasPolizaResults, setHasPolizaResults] = useState(false);

    React.useImperativeHandle(ref, () => ({
        getPolizaData: () => polizaData,
        getDefinicionSeleccionada: () => definicionSeleccionada,
        isPolizaGenerada: () => polizaGenerada,
        getPolizaEstado: () => polizaEstado
    }));

    useEffect(() => {
        loadDefinicionesPoliza();
    }, []);

    useEffect(() => {
        (async () => {
            if (header?.num_periodo > 0) {
                await refreshData();
                // Cargar póliza existente si hay una definición seleccionada
                if (definicionSeleccionada) {
                    await loadExistingPoliza();
                }
            }
        })();
    }, [header]);

    useEffect(() => {
        (async () => {
            if (definicionSeleccionada && header?.num_periodo > 0) {
                await loadExistingPoliza();
            }
        })();
    }, [definicionSeleccionada]);

    const loadDefinicionesPoliza = async () => {
        setLoadingDefiniciones(true);
        try {
            const response = await getAll();
            if (response && response.ok && response.payload) {
                setDefinicionesPoliza(response.payload);
                // Si solo hay una definición, seleccionarla automáticamente
                if (response.payload.length === 1) {
                    setDefinicionSeleccionada(response.payload[0].id);
                }
            } else {
                message.error('No se pudieron cargar las definiciones de póliza');
            }
        } catch (error) {
            console.error('Error al cargar definiciones de póliza:', error);
            message.error('Error al cargar las definiciones de póliza');
        } finally {
            setLoadingDefiniciones(false);
        }
    };

    const loadExistingPoliza = async () => {
        if (!definicionSeleccionada || !header?.periodo_id || !header?.anio || !header?.num_periodo) {
            return null;
        }

        setLoadingPoliza(true);
        try {
            const response = await getPoliza(
                definicionSeleccionada,
                header.periodo_id,
                header.anio,
                header.num_periodo
            );

            setPolizaObject(null);
            setPolizaData(null);
            setPolizaEstado(null);
            setPolizaGenerada(false);

            if (response?.ok) {
                if (response.payload && response.payload.conceptos && Array.isArray(response.payload.conceptos) && response.payload.conceptos.length > 0) {
                    const processedData = processPolizaData(response.payload.conceptos);
                    if (processedData) {
                        setPolizaObject(response.payload.data);
                        setPolizaData(processedData);
                        setPolizaEstado(response.payload.estado);
                        setPolizaGenerada(true);
                        console.log('Póliza existente cargada correctamente');
                        return response.payload.data || null;
                    }
                }
            }
            return null;
        } catch (error) {
            console.error('Error al cargar póliza existente:', error);
            setPolizaData(null);
            setPolizaEstado(null);
            setPolizaGenerada(false);
            return null;
        } finally {
            setLoadingPoliza(false);
        }
    };

    const refreshData = async (id) => {
        setLoading(true);
        const responseEncabezado = await recibosEncabezadoNomina(header.periodo_id, header.anio, header.num_periodo);
        if (responseEncabezado.ok) {
            let data = responseEncabezado.payload;
            setData(data);
            setHasPolizaResults(data && data.length > 0);
        } else {
            setHasPolizaResults(false);
        }

        const response = await bitacoraProcesoNomina(header.periodo_id, header.anio, header.num_periodo);
        if (response.ok) {
            let info = response.payload;
            setInfo(info);
        }
        setLoading(false);
    };

    // Función para procesar los datos de la API y crear tabla con agrupación por concepto
    const processPolizaData = (conceptos) => {
        if (!conceptos || !Array.isArray(conceptos)) {
            return null;
        }

        // Buscar concepto sin nombre para mostrar en el balance
        const conceptoSinNombre = conceptos.find(item =>
            (!item.concepto_clave || item.concepto_clave.trim() === '') &&
            (!item.concepto || item.concepto.trim() === '')
        );

        // Agrupar conceptos por concepto_id y tipo (cargo/abono)
        const conceptosAgrupados = {};

        conceptos.forEach((item, index) => {
            // Identificador único para cada concepto
            const isBalance = item.isBalance || false;
            const conceptoKey = isBalance
                ? 'BALANCE'
                : `${item.concepto_clave || 'SIN_CLAVE'}_${item.cargo > 0 ? 'CARGO' : 'ABONO'}`;

            if (!conceptosAgrupados[conceptoKey]) {
                let concepto = '';
                if (item.concepto && item.concepto.trim() !== '') {
                    concepto = `${item.concepto_clave || ''} - ${item.concepto}`;
                } else {
                    concepto = isBalance ? 'BALANCE' : 'Sin concepto';
                }

                conceptosAgrupados[conceptoKey] = {
                    key: conceptoKey,
                    concepto,
                    concepto_clave: item.concepto_clave,
                    isBalance,
                    cuenta: item.cuenta_nombre || 'Sin cuenta',
                    cargo: 0,
                    abono: 0,
                    analiticas: []
                };
            }

            // Agregar analítica si existe
            if (item.analitica && item.analitica.analitica_nombre) {
                conceptosAgrupados[conceptoKey].analiticas.push({
                    analitica_id: item.analitica.analitica_id,
                    analitica_nombre: item.analitica.analitica_nombre,
                    cargo: parseFloat(item.cargo) || 0,
                    abono: parseFloat(item.abono) || 0
                });
            }

            // Sumar totales
            conceptosAgrupados[conceptoKey].cargo += parseFloat(item.cargo) || 0;
            conceptosAgrupados[conceptoKey].abono += parseFloat(item.abono) || 0;
        });

        // Convertir a array
        const processedData = Object.values(conceptosAgrupados);

        let totalCargos = 0;
        let totalAbonos = 0;
        // Calcular totales
        processedData.filter(x => !x.isBalance).forEach(({ cargo, abono }) => {
            totalCargos += parseFloat(cargo) || 0;
            totalAbonos += parseFloat(abono) || 0;
        });
        const balance = totalCargos - totalAbonos;

        return {
            data: processedData,
            resumen: {
                totalCargos: totalCargos,
                totalAbonos: totalAbonos,
                balance: balance,
                conceptoSinNombre: conceptoSinNombre
            }
        };
    };

    const handleDefinicionChange = (value) => {
        setDefinicionSeleccionada(value);
        // Limpiar póliza anterior si se cambia la definición
        setPolizaGenerada(false);
        setPolizaData(null);
        setPolizaEstado(null);
    };

    const handleGenerarPoliza = async () => {
        if (!definicionSeleccionada) {
            message.warning('Por favor selecciona una definición de póliza');
            return;
        }

        if (!header || !header.periodo_id || !header.anio || !header.num_periodo) {
            message.warning('Por favor completa los datos del período');
            return;
        }

        if (!hasPolizaResults) {
            message.warning('No hay resultados disponibles para generar la póliza');
            return;
        }

        setLoadingPoliza(true);

        try {
            // Llamada real a la API
            const response = await processPoliza(
                definicionSeleccionada,
                header.periodo_id,
                header.anio,
                header.num_periodo
            );

            if (response?.ok && response?.payload) {
                // Procesar la nueva estructura de respuesta
                if (Array.isArray(response.payload.conceptos)) {
                    const processedData = processPolizaData(response.payload.conceptos);
                    if (processedData) {
                        const polizaResponseData = response.payload.data || null;
                        setPolizaObject(polizaResponseData);
                        setPolizaData(processedData);
                        setPolizaEstado(response.payload.estado);
                        setPolizaGenerada(true);

                        if (!polizaResponseData?.id) {
                            await loadExistingPoliza();
                        }

                        message.success('Póliza generada correctamente!');
                    } else {
                        message.error('Error al procesar los datos de la póliza');
                    }
                } else {
                    message.error('Formato de respuesta de póliza no válido');
                }
            } else {
                message.error(response?.message || 'Error al generar la póliza');
            }
        } catch (error) {
            console.error('Error al generar póliza:', error);
            message.error('Error al generar la póliza');
        } finally {
            setLoadingPoliza(false);
        }
    };

    const onClickSendToOdoo = async () => {
        let polizaId = polizaObject?.id;

        if (!polizaId) {
            const loadedPoliza = await loadExistingPoliza();
            polizaId = loadedPoliza?.id;
        }

        if (!polizaId) {
            message.warning('Primero debes generar una póliza válida para enviar a Odoo');
            return;
        }

        setLoadingEnviando(true);
        try {
            // Llamada real a la API
            const response = await sendToOdooPoliza(polizaId);

            if (response?.ok && response?.payload) {
                setPolizaEstado(response.payload.estado);
                await loadExistingPoliza();
                message.success('Póliza enviada a Odoo correctamente!');
            } else {
                message.error(response?.message || 'Error al enviar la póliza a Odoo');
            }
        } catch (error) {
            console.error('Error al enviar póliza a Odoo:', error);
            message.error('Error al enviar la póliza a Odoo');
        } finally {
            setLoadingEnviando(false);
        }
    };


    const openDetails = async (object) => {
        setOpenDetail(true);
        setPeriodSelected(object);
    };

    const getFile = async (object, file) => {
        await downloadRecibo(file, header.periodo_id, header.anio, header.num_periodo, object.odoo_empleado_id);
    };

    const columns = [
        {
            title: 'Empleado',
            dataIndex: 'empleado',
            key: 'empleado',
        },
        {
            title: 'Etapa',
            dataIndex: 'estatus',
            key: 'estatus',
            render: (item, object) => {
                if (object.estatus == status_recibo_nomina.ERROR)
                    return <>
                        <Tag color={'#f50'} key={'error'}>
                            {'Con Error'}
                        </Tag>
                        <Tooltip title={object.error}>
                            <InfoCircleOutlined />
                        </Tooltip>
                    </>;

                if (object.estatus == status_recibo_nomina.EN_PROCESO)
                    return <Tag color={'#2db7f5'} key={'en_proceso'}>
                        {'En proceso'}
                    </Tag>;

                if (object.estatus == status_recibo_nomina.PROCESADO)
                    return <Tag color={'#87d068'} key={'PROCESADO'}>
                        {'Procesado'}
                    </Tag>;
                if (object.estatus == status_recibo_nomina.CERRADO)
                    return <Tag color={'#108ee9'} key={'Cerrado'}>
                        {'Cerrado'}
                    </Tag>;

                if (object.estatus == status_recibo_nomina.TIMBRADO)
                    return <Tag color={'purple'} key={'Timbrado'}>
                        {'Timbrado'}
                    </Tag>;

                return '';
            }
        },
        {
            title: 'Estado',
            dataIndex: 'success',
            key: 'success',
            render: (item, object) => {
                if (item) {
                    return <CheckOutlined style={{ color: 'green' }} />;
                }
                if (!item) {
                    return <CloseOutlined style={{ color: 'red' }} />;
                }
                return <></>;
            }
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            render: (item, object) => {
                return <Space>
                    {
                        object.estatus == status_recibo_nomina.TIMBRADO && <>
                            <Button type='primary' size='small' onClick={() => {
                                getFile(object, 'xml');
                            }}>XML</Button>
                            <Button type='primary' size='small' onClick={() => {
                                getFile(object, 'pdf');
                            }}>PDF</Button>
                        </>
                    }
                </Space>;
            }
        }
    ];

    const polizaColumns = [
        {
            title: 'Concepto',
            dataIndex: 'concepto',
            key: 'concepto',
            width: '40%',
        },
        {
            title: 'Cuenta',
            dataIndex: 'cuenta',
            key: 'cuenta',
            width: '25%',
        },
        {
            title: 'Cargo',
            dataIndex: 'cargo',
            key: 'cargo',
            align: 'right',
            width: '17.5%',
            render: (value) => <Currency value={value} />,
        },
        {
            title: 'Abono',
            dataIndex: 'abono',
            key: 'abono',
            align: 'right',
            width: '17.5%',
            render: (value) => <Currency value={value} />,
        },
    ];

    const expandedRowRender = (record) => {
        if (!record.analiticas || record.analiticas.length === 0) {
            return null;
        }

        const analiticasColumns = [
            {
                title: 'Analítica',
                dataIndex: 'analitica_nombre',
                key: 'analitica_nombre',
                width: '65%',
                render: (text) => <span style={{ paddingLeft: 24, fontStyle: 'italic' }}>→ {text}</span>
            },
            {
                title: 'Cargo',
                dataIndex: 'cargo',
                key: 'cargo',
                align: 'right',
                width: '17.5%',
                render: (value) => <Currency value={value} />,
            },
            {
                title: 'Abono',
                dataIndex: 'abono',
                key: 'abono',
                align: 'right',
                width: '17.5%',
                render: (value) => <Currency value={value} />,
            },
        ];

        return (
            <Table
                columns={analiticasColumns}
                dataSource={record.analiticas}
                pagination={false}
                showHeader={false}
                size="small"
                rowKey="analitica_id"
                style={{ marginLeft: 0 }}
            />
        );
    };

    function currencyFormat(num) {
        if (typeof num == "string") {
            num = parseFloat(num)
        }
        if (!num) {
            return '$0';
        }
        return '$' + num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
    }

    // Función para obtener el estado visual de la póliza
    const getEstadoPolizaTag = () => {
        if (!polizaEstado) return null;

        switch (polizaEstado.toLowerCase()) {
            case 'procesada':
                return <Tag color="green">Procesada</Tag>;
            case 'pendiente':
                return <Tag color="orange">Pendiente</Tag>;
            case 'enviada_odoo':
                return <Tag color="purple">Enviada a Odoo</Tag>;
            case 'error':
                return <Tag color="red">Error</Tag>;
            default:
                return <Tag>{polizaEstado}</Tag>;
        }
    };

    return (
        <>
            {/* Estadísticas de nómina */}
            {info &&
                <Row gutter={16} style={{ paddingTop: 10, paddingBottom: 10 }}>
                    <Col span={7} offset={1}>
                        <Statistic title="Procesados" value={info.usuarios_procesados} suffix={`/ ${info.usuarios_esperados}`} />
                    </Col>
                    <Col span={7}>
                        <Statistic title="Correctos" value={info.usuarios_procesados_exito} suffix={`/ ${info.usuarios_esperados}`} prefix={<CheckOutlined style={{ color: 'green' }} />} />
                    </Col>
                    <Col span={7}>
                        <Statistic title="Incorrectos" value={info.usuarios_procesados_error} suffix={`/ ${info.usuarios_esperados}`} prefix={<CloseOutlined style={{ color: 'red' }} />} />
                    </Col>
                </Row>
            }

            {/* Selector de Definición de Póliza */}
            <Row style={{ marginTop: 16, marginBottom: 16 }}>
                <Col span={24}>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ fontWeight: 'bold' }}>Definición de Póliza:</label>
                    </div>
                    <Space>
                        <Select
                            placeholder="Selecciona una definición de póliza"
                            style={{ width: 400 }}
                            value={definicionSeleccionada}
                            onChange={handleDefinicionChange}
                            loading={loadingDefiniciones}
                            disabled={disabledForm}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {definicionesPoliza.map(definicion => (
                                <Option key={definicion.id} value={definicion.id}>
                                    {`${definicion.clave} - ${definicion.descripcion}`}
                                </Option>
                            ))}
                        </Select>
                        <Button
                            type="primary"
                            onClick={handleGenerarPoliza}
                            loading={loadingPoliza}
                            disabled={!definicionSeleccionada || !hasPolizaResults || polizaEstado === 'enviada_odoo' || loadingEnviando}
                        >
                            {loadingPoliza ? 'Generando...' : 'Generar Póliza'}
                        </Button>
                        <Button
                            type="primary"
                            onClick={onClickSendToOdoo}
                            loading={loadingEnviando}
                            icon={!loadingEnviando && <SendOutlined />}
                            disabled={
                                !polizaGenerada ||
                                !polizaData ||
                                polizaEstado === 'enviada_odoo' ||
                                loadingEnviando
                            }
                        >
                            {loadingEnviando ? 'Enviando a Odoo...' : 'Enviar a Odoo'}
                        </Button>
                    </Space>
                </Col>
            </Row>

            {/* Mensaje informativo cuando no hay resultados */}
            {!hasPolizaResults && definicionSeleccionada && (
                <Card style={{ marginBottom: 16, backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <InfoCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                        <span style={{ color: '#d48806' }}>
                            No hay resultados de nómina disponibles para generar la póliza.
                        </span>
                    </div>
                </Card>
            )}

            {/* Contenido de póliza generada */}
            {polizaData && (
                <div style={{ marginBottom: 20 }}>
                    {/* Resumen de la póliza */}
                    <Card title="Resumen de Póliza" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={5}>
                                <Statistic
                                    title="Definición Utilizada"
                                    value={definicionesPoliza.find(d => d.id === definicionSeleccionada)?.descripcion || 'N/A'}
                                    valueStyle={{ fontSize: '14px' }}
                                />
                            </Col>
                            <Col span={4}>
                                <div style={{ marginBottom: 8 }}>
                                    <div style={{ fontSize: '14px', color: '#8c8c8c' }}>Estado</div>
                                    {getEstadoPolizaTag()}
                                </div>
                            </Col>
                            <Col span={5}>
                                <Statistic
                                    title="Total Cargos"
                                    value={currencyFormat(polizaData.resumen.totalCargos)}
                                    precision={2}
                                />
                            </Col>
                            <Col span={5}>
                                <Statistic
                                    title="Total Abonos"
                                    value={currencyFormat(polizaData.resumen.totalAbonos)}
                                    precision={2}
                                />
                            </Col>
                            <Col span={5}>
                                <Statistic
                                    title="Balance"
                                    value={
                                        polizaData.resumen.conceptoSinNombre
                                            ? currencyFormat(
                                                parseFloat(polizaData.resumen.conceptoSinNombre.abono) !== 0
                                                    ? polizaData.resumen.conceptoSinNombre.abono
                                                    : polizaData.resumen.conceptoSinNombre.cargo
                                            )
                                            : currencyFormat(polizaData.resumen.balance)
                                    }
                                    valueStyle={{ fontSize: '18px' }}
                                />
                            </Col>
                        </Row>
                    </Card>

                    {/* Tabla simplificada de póliza */}
                    <Card title="Detalle de Póliza" style={{ marginBottom: 16 }}>
                        <style jsx>{`
                            .poliza-table .ant-table-summary .ant-table-cell {
                                padding: 8px 16px !important;
                                font-weight: bold;
                            }
                            .poliza-table .ant-table-summary .ant-table-cell:nth-child(3),
                            .poliza-table .ant-table-summary .ant-table-cell:nth-child(4) {
                                text-align: right !important;
                            }
                            .poliza-table .ant-table-expanded-row > td {
                                padding: 0 !important;
                                background-color: #fafafa;
                            }
                            .poliza-table .ant-table-expanded-row .ant-table {
                                margin: 0;
                            }
                            .poliza-table .ant-table-expanded-row .ant-table-tbody > tr > td {
                                border-bottom: none;
                                padding: 4px 16px;
                            }
                        `}</style>

                        <Table
                            className="poliza-table"
                            dataSource={polizaData.data}
                            columns={polizaColumns}
                            pagination={false}
                            size="small"
                            expandable={{
                                expandedRowRender: expandedRowRender,
                                rowExpandable: (record) => record.analiticas && record.analiticas.length > 0,
                                expandIcon: ({ expanded, onExpand, record }) => {
                                    if (!record.analiticas || record.analiticas.length === 0) {
                                        return <span style={{ display: 'inline-block', width: 17 }} />;
                                    }
                                    return expanded ? (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<span>▼</span>}
                                            onClick={e => onExpand(record, e)}
                                            style={{ padding: 0 }}
                                        />
                                    ) : (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<span>▶</span>}
                                            onClick={e => onExpand(record, e)}
                                            style={{ padding: 0 }}
                                        />
                                    );
                                },
                            }}
                            summary={(pageData) => {
                                let totalCargo = 0;
                                let totalAbono = 0;

                                pageData.forEach(({ cargo, abono }) => {
                                    totalCargo += parseFloat(cargo) || 0;
                                    totalAbono += parseFloat(abono) || 0;
                                });

                                return (
                                    <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                                        {/* Celda para el ícono de expansión */}
                                        <Table.Summary.Cell index={0} />
                                        {/* Celda para Concepto y Cuenta combinadas */}
                                        <Table.Summary.Cell index={1} colSpan={2}> TOTALES </Table.Summary.Cell>
                                        {/* Celda para Cargo */}
                                        <Table.Summary.Cell index={3} style={{ textAlign: 'right' }}>
                                            <Currency value={totalCargo} />
                                        </Table.Summary.Cell>
                                        {/* Celda para Abono */}
                                        <Table.Summary.Cell index={4} style={{ textAlign: 'right' }}>
                                            <Currency value={totalAbono} />
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                );
                            }}
                        />
                    </Card>

                    {/* Advertencia de balance */}
                    {/* {Math.abs(polizaData.resumen.balance) >= 0.01 && (
                        <Card style={{ marginTop: 16, backgroundColor: '#fff2f0', borderColor: '#ffccc7' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ExclamationCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                                <span style={{ color: '#ff4d4f' }}>
                                    Advertencia: La póliza no está balanceada.
                                    Diferencia: ${Math.abs(polizaData.resumen.balance).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        </Card>
                    )} */}

                    {/* Mensaje informativo cuando la póliza no está procesada */}
                    {polizaEstado && polizaEstado !== 'procesada' && (
                        <Card style={{ marginTop: 16, backgroundColor: '#fffbe6', borderColor: '#ffe58f' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <InfoCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                <span style={{ color: '#d48806' }}>
                                    La póliza debe estar en estado "procesada" para poder enviarla a Odoo.
                                </span>
                            </div>
                        </Card>
                    )}
                </div>
            )}

            {/* Tabla de empleados */}
            {/* {loading ? <Skeleton active={loading} /> : <>
                {data.length === 0 ? <></> :
                    <Card title="Estado de Empleados">
                        <Table dataSource={data} columns={columns} pagination={{ pageSize: 10 }} />
                    </Card>
                }
            </>} */}
        </>
    );
});

export default Result;